import {determineLogBase} from '../../../util/plotHelpers/axis';
import {type DomainMaybe} from '../types';

/**
 * Prepare a domain for log-scale rendering.
 *
 * Log scales cannot represent zero or negative values, so this function:
 * - Preserves positive finite values as-is, even very small ones (e.g. 1e-8).
 *   This fixes small values being silently clamped to a hardcoded 1e-4.
 * - When min is zero or negative, derives a proportional floor from max using
 *   the same base-stepping heuristic react-vis uses for log axes.
 * - Passes through Infinity, NaN, and null unchanged to avoid changing
 *   edge-case semantics (see TODO in getDomain.test.ts).
 */
export function getDomain(domain: DomainMaybe, logScale: boolean): DomainMaybe {
  if (!logScale) {
    return domain;
  }

  const [rawMin, rawMax] = domain;
  const min =
    rawMin == null
      ? null
      : isPositiveFinite(rawMin)
        ? rawMin
        : isPositiveFinite(rawMax)
          ? deriveLogFloor(rawMax)
          : rawMin;

  return [min, rawMax];
}

function isPositiveFinite(value: number | null): value is number {
  return value != null && Number.isFinite(value) && value > 0;
}

/**
 * Derive a reasonable lower bound from the maximum so the log axis has a
 * useful range. Uses the same base-stepping algorithm as react-vis log axes
 * (divides by 100 each step until base < max/10).
 */
function deriveLogFloor(max: number): number {
  return determineLogBase([0, max]);
}
